import {Card, Form, Row, Col, Button, Spinner} from 'react-bootstrap'
import {HSDescriptionDetail} from '../core/_models'
import SummernoteEditor from '../../../../../ui-kit/SummerNote/SummerNote'

type Props = {
  isLoadingHS: boolean
  hsOrigin: HSDescriptionDetail
  hsPending: HSDescriptionDetail
  isDifferent: (val1: any, val2: any) => boolean
  handleChange: (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>
  ) => void
  handleSelectChange: (e: React.ChangeEvent<HTMLSelectElement>) => void
  handleCancel: () => void
  handleSubmit: () => void
  isLoadingButton: boolean
  isFormValid: boolean
}

const HSDescriptionComparisonForm: React.FC<Props> = ({
  isLoadingHS,
  hsOrigin,
  hsPending,
  isDifferent,
  handleChange,
  handleSelectChange,
  handleCancel,
  handleSubmit,
  isLoadingButton,
  isFormValid,
}) => {
  return (
    <Row className='mb-5'>
      <Col>
        <Card>
          <Card.Header className='card-header-stretch'>
            <Card.Title className='w-100'>
              <div className='title-container w-100 d-flex justify-content-between align-items-center'>
                <div className='title d-flex align-items-center'>
                  <span className='fa fa-balance-scale px-2'></span>
                  <h3 className='text-gray-700'>Review HS Description Differences</h3>
                </div>
              </div>
            </Card.Title>
          </Card.Header>
          <Card.Body>
            {!isLoadingHS ? (
              <Form className='form-compare-hs'>
                {/* HEADERS */}
                <Row className='mb-4 pb-2 border-bottom'>
                  <Col md={6}>
                    <h4 className='text-muted text-center'>Origin Content</h4>
                  </Col>
                  <Col md={6}>
                    <h4 className='text-primary text-center'>Pending Review Content</h4>
                  </Col>
                </Row>

                {/* GENERAL INFORMATION */}
                <h3 className='mb-4 mt-5'>General Information</h3>

                <Row className='mb-4'>
                  <Col md={6}>
                    <Form.Label className='text-muted me-0 ps-0' column>
                      ID
                    </Form.Label>
                    <Form.Control disabled type='text' value={hsOrigin.id || ''} />
                  </Col>
                  <Col md={6}>
                    <Form.Label className='text-primary me-0 ps-0' column>
                      <span
                        className={`required ${
                          isDifferent(hsOrigin.id, hsPending.id) ? 'label-highlight' : ''
                        }`}
                      >
                        ID
                      </span>
                    </Form.Label>
                    <Form.Control
                      type='text'
                      name='id'
                      className='required'
                      value={hsPending.id || ''}
                      onChange={handleChange}
                    />
                  </Col>
                </Row>

                <Row className='mb-4'>
                  <Col md={6}>
                    <Form.Label className='text-muted me-0 ps-0' column>
                      Version
                    </Form.Label>
                    <Form.Control disabled type='text' value={hsOrigin.version || ''} />
                  </Col>
                  <Col md={6}>
                    <Form.Label className='text-primary me-0 ps-0' column>
                      <span
                        className={`required ${
                          isDifferent(hsOrigin.version, hsPending.version) ? 'label-highlight' : ''
                        }`}
                      >
                        Version
                      </span>
                    </Form.Label>
                    <Form.Control
                      type='text'
                      name='version'
                      className='required'
                      value={hsPending.version || ''}
                      onChange={handleChange}
                    />
                  </Col>
                </Row>

                <Row className='mb-4'>
                  <Col md={12}>
                    <Form.Label className='text-muted me-0 ps-0' column>
                      Origin Description
                    </Form.Label>
                    <div
                      className='data-value bg-light p-3 border rounded mb-4'
                      style={{minHeight: '100px'}}
                      dangerouslySetInnerHTML={{__html: hsOrigin.description || '-'}}
                    />
                  </Col>
                  <Col md={12}>
                    <Form.Label className='text-primary me-0 ps-0' column>
                      <span
                        className={
                          isDifferent(hsOrigin.description, hsPending.description)
                            ? 'label-highlight'
                            : ''
                        }
                      >
                        Pending Description
                      </span>
                    </Form.Label>
                    <SummernoteEditor
                      value={hsPending.description || ''}
                      onChange={(html) => {
                        const fakeEvent = {
                          target: {name: 'description', value: html},
                        } as any
                        handleChange(fakeEvent)
                      }}
                    />
                  </Col>
                </Row>

                <Row className='mb-4'>
                  <Col md={12}>
                    <Form.Label className='text-muted me-0 ps-0' column>
                      Origin Description (FR)
                    </Form.Label>
                    <div
                      className='data-value bg-light p-3 border rounded mb-4'
                      style={{minHeight: '100px'}}
                      dangerouslySetInnerHTML={{__html: hsOrigin.description_fr || '-'}}
                    />
                  </Col>
                  <Col md={12}>
                    <Form.Label className='text-primary me-0 ps-0' column>
                      <span
                        className={
                          isDifferent(hsOrigin.description_fr, hsPending.description_fr)
                            ? 'label-highlight'
                            : ''
                        }
                      >
                        Pending Description (FR)
                      </span>
                    </Form.Label>
                    <SummernoteEditor
                      value={hsPending.description_fr || ''}
                      onChange={(html) => {
                        const fakeEvent = {
                          target: {name: 'description_fr', value: html},
                        } as any
                        handleChange(fakeEvent)
                      }}
                    />
                  </Col>
                </Row>

                <hr />
                {/* ATTRIBUTES */}
                <h3 className='mb-4 mt-5'>Attributes</h3>

                <Row className='mb-4'>
                  <Col md={6}>
                    <Form.Label className='text-muted me-0 ps-0' column>
                      Level
                    </Form.Label>
                    <Form.Control disabled type='text' value={hsOrigin.level ?? ''} />
                  </Col>
                  <Col md={6}>
                    <Form.Label className='text-primary me-0 ps-0' column>
                      <span
                        className={`required ${
                          isDifferent(hsOrigin.level, hsPending.level) ? 'label-highlight' : ''
                        }`}
                      >
                        Level
                      </span>
                    </Form.Label>
                    <Form.Control
                      type='text'
                      name='level'
                      className='required'
                      value={hsPending.level ?? ''}
                      onChange={handleChange}
                    />
                  </Col>
                </Row>

                <Row className='mb-4'>
                  <Col md={6}>
                    <Form.Label className='text-muted me-0 ps-0' column>
                      Parent
                    </Form.Label>
                    <Form.Control disabled type='text' value={hsOrigin.parent ?? ''} />
                  </Col>
                  <Col md={6}>
                    <Form.Label className='text-primary me-0 ps-0' column>
                      <span
                        className={
                          isDifferent(hsOrigin.parent, hsPending.parent) ? 'label-highlight' : ''
                        }
                      >
                        Parent
                      </span>
                    </Form.Label>
                    <Form.Control
                      type='text'
                      name='parent'
                      value={hsPending.parent ?? ''}
                      onChange={handleChange}
                    />
                  </Col>
                </Row>

                <div className='row pt-5'>
                  <div className='col-lg-12 d-flex justify-content-end gap-3'>
                    <Button variant='default' className='btn-cancel' onClick={handleCancel}>
                      Cancel
                    </Button>
                    <Button
                      variant='primary'
                      onClick={handleSubmit}
                      disabled={isLoadingButton || !isFormValid}
                    >
                      {isLoadingButton ? (
                        <>
                          <Spinner animation='border' size='sm' className='me-2' />
                          Submit
                        </>
                      ) : (
                        'Submit'
                      )}
                    </Button>
                  </div>
                </div>
              </Form>
            ) : (
              <div
                className='d-flex align-items-center justify-content-center'
                style={{height: '50vh'}}
              >
                <Spinner animation='border' role='status' variant='primary' className='me-3' />
                <div className='loading-text'>
                  Loading
                  <span className='dot'>.</span>
                  <span className='dot'>.</span>
                  <span className='dot'>.</span>
                </div>
              </div>
            )}
          </Card.Body>
        </Card>
      </Col>
    </Row>
  )
}

export default HSDescriptionComparisonForm
